// Avisos del servidor (documento 05, WebSocket). La app no depende del socket para operar: si se cae, el
// sincronizador igual recarga cada 30 s. El chat (que se carga aparte) se anota con `escuchar` y usa `emitir`.
import { io, type Socket } from 'socket.io-client';
import { API_URL } from '../config';
import { api } from '../datos/api';
import { db } from '../datos/db';
import { refrescarPaquete } from '../datos/paquete';

export type AvisoTiempoReal =
  | { tipo: 'ruta_actualizada'; motivo: string; por: string | null }
  | { tipo: 'rendicion_recibida'; por: string | null }
  | { tipo: 'notificacion'; titulo: string };

let socket: Socket | null = null;
let recarga: ReturnType<typeof setTimeout> | null = null;
// Oyentes de otros módulos (el chat): sobreviven a reconexiones y a un socket nuevo.
const oyentes = new Map<string, Set<(datos: unknown) => void>>();

const recargar = () => {
  if (recarga) clearTimeout(recarga);
  recarga = setTimeout(() => void refrescarPaquete().catch(() => undefined), 500);
};

// A la vista o no (decisión D40): con la app a la vista el servidor no manda push.
const avisarVisible = () => socket?.emit('visible', { visible: document.visibilityState === 'visible' });

export async function conectarTiempoReal(alAviso: (a: AvisoTiempoReal) => void): Promise<() => void> {
  desconectarTiempoReal();
  const s = await db.sesion.get('actual');
  if (!s?.access_token) return () => undefined;
  const actual = io(`${API_URL}/rt`, {
    auth: { token: s.access_token, movil_id: s.movil_id ?? undefined },
    transports: ['websocket', 'polling'],
    reconnectionDelayMax: 30_000,
  });
  socket = actual;
  actual.on('ruta.actualizada', (d: { motivo?: string; por?: string }) => {
    recargar();
    alAviso({ tipo: 'ruta_actualizada', motivo: d.motivo ?? 'actualizada', por: d.por ?? null });
  });
  actual.on('ruta.estado', recargar);
  // Control de la carga (decisión D54): lo que tilda el encargado, sus conformidades, un pedido que entra o sale.
  actual.on('carga.actualizada', recargar);
  actual.on('parada.resultado', (d: { deshecha?: boolean }) => d.deshecha && recargar());
  actual.on('rendicion.recibida', (d: { recibida_por?: string | null }) => {
    recargar();
    alAviso({ tipo: 'rendicion_recibida', por: d.recibida_por ?? null });
  });
  actual.on('notificacion', (d: { titulo: string }) => alAviso({ tipo: 'notificacion', titulo: d.titulo }));
  actual.onAny((evento: string, datos: unknown) => oyentes.get(evento)?.forEach((f) => f(datos)));
  actual.on('connect', () => oyentes.get('connect')?.forEach((f) => f(undefined)));
  // "listo" llega cuando el servidor terminó de reconocer la sesión: recién ahí sirve avisar si está a la vista (antes
  // el aviso se pierde y el servidor mandaría push con la app abierta).
  actual.on('listo', avisarVisible);
  actual.on('disconnect', () => oyentes.get('disconnect')?.forEach((f) => f(undefined)));
  // El token vence a los 15 minutos: al reconectar se usa el vigente.
  actual.io.on('reconnect_attempt', async () => {
    const vigente = await db.sesion.get('actual');
    if (vigente) actual.auth = { token: vigente.access_token, movil_id: vigente.movil_id ?? undefined };
  });
  // Token vencido (por ejemplo, después de horas sin señal): el servidor corta y no reintenta solo. Una petición
  // cualquiera renueva el token y se vuelve a conectar.
  let intentos = 0;
  actual.on('error_auth', () => {
    if (++intentos > 3) return;
    setTimeout(async () => {
      if (socket !== actual) return;
      await api('/auth/yo').catch(() => undefined);
      const vigente = await db.sesion.get('actual');
      if (!vigente?.access_token || socket !== actual) return;
      actual.auth = { token: vigente.access_token, movil_id: vigente.movil_id ?? undefined };
      actual.connect();
    }, 2000 * intentos);
  });
  actual.on('connect', () => (intentos = 0));
  document.addEventListener('visibilitychange', avisarVisible);
  return desconectarTiempoReal;
}

export function cambiarMovil(movilId: string): void {
  socket?.emit('movil', { movil_id: movilId });
}

export function desconectarTiempoReal(): void {
  document.removeEventListener('visibilitychange', avisarVisible);
  socket?.disconnect();
  socket = null;
}

/** Escucha un evento del servidor (o `connect` y `disconnect`). Devuelve cómo dejar de escuchar. */
export function escuchar<T>(evento: string, f: (datos: T) => void): () => void {
  const lista = oyentes.get(evento) ?? new Set();
  const g = f as (datos: unknown) => void;
  lista.add(g);
  oyentes.set(evento, lista);
  return () => void lista.delete(g);
}

/** Aviso liviano al servidor ("escribiendo…"). Sin conexión no se manda: no se guarda para después. */
export function emitir(evento: string, datos: unknown): void {
  if (socket?.connected) socket.emit(evento, datos);
}

export const conectado = (): boolean => !!socket?.connected;
